import { useWindowDimensions } from "react-native";

// Shared responsive primitive for the lift. The same component tree renders
// on a phone and on the web; the ONLY thing that should change between them
// is layout, driven by the available width — never a Platform.OS branch.
//
//   narrow  → phone, or a browser window narrower than the threshold:
//             the vertical, single-column mobile experience.
//   wide    → a desktop-class viewport: room for side-by-side regions,
//             a left nav rail, master/detail, multi-column grids.
//
// useWindowDimensions is RN core (works on react-native-web too, where it
// subscribes to window resize), so a desktop user who shrinks the window
// degrades to the exact same narrow layout the phone uses — one experience,
// adapted to the device, not two codebases.
export const WIDE_BREAKPOINT = 768;

// The second threshold (#336 slice 6, brief §7.3): from here a surface with
// room for three zones lays them side by side. Between the two a wide surface
// keeps two. Only the program editor reads it today; `wide` is unchanged.
export const LARGE_BREAKPOINT = 1100;

/** narrow < WIDE_BREAKPOINT <= medium < LARGE_BREAKPOINT <= large. */
export type BreakpointTier = "narrow" | "medium" | "large";

// Max width for a single-column form / detail pane on a wide viewport, so a
// form doesn't stretch to unreadable line lengths on a large monitor. Shared by
// the Settings detail pane and the Me content pane (both cap the same way).
export const DETAIL_PANE_MAX_WIDTH = 720;

export type Breakpoint = {
  width: number;
  /** Desktop-class viewport (>= WIDE_BREAKPOINT). */
  wide: boolean;
  /** Phone-class / narrow-browser viewport. */
  narrow: boolean;
  /** Which of the three widths: `narrow` is the same as the flag above. */
  tier: BreakpointTier;
};

/** The tier of a width: the window's here, a box's own elsewhere (#358). */
export const tierOf = (width: number): BreakpointTier =>
  width < WIDE_BREAKPOINT ? "narrow" : width < LARGE_BREAKPOINT ? "medium" : "large";

export function useBreakpoint(): Breakpoint {
  const { width } = useWindowDimensions();
  const tier = tierOf(width);
  return { width, wide: tier !== "narrow", narrow: tier === "narrow", tier };
}
